import {
  ArrowDownOutlined,
  CloseOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  EllipsisOutlined,
  ExportOutlined,
  FilterOutlined,
  LayoutOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  theme,
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  Tag,
  Space,
  Popover,
  Listy,
  Typography,
  Checkbox,
  Switch,
  Radio,
  Badge,
  Dropdown,
} from "antd";
import { useEffect, useState } from "react";
import useTableView from "../../common/tableviews/TableViews";
import { withTooltip } from "../../common/common";
import { ArrowDownUp, Columns3Cog } from "lucide-react";

const SwitchItem = ({ value, onChange }) => (
  <div style={{ display: "flex", padding: "8px 3px" }}>
    <div style={{ flex: 1 }}>{value.title}</div>
    <Switch
      size="small"
      value={value.visible}
      onChange={(nv) => {
        onChange({ ...value, visible: nv });
      }}
    />
  </div>
);

const SelectColumnsPopover = ({ value, onChange, children }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    form.setFieldValue("columns", value);
  }, [value]);

  const onFinish = (data) => {
    onChange(data["columns"]);
    setOpen(false);
  };

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
      content={
        <div style={{ minWidth: 250 }}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Typography.Text strong style={{ flex: 1 }}>
              Personalizza colonne
            </Typography.Text>
            <Button icon={<CloseOutlined />} size="small" onClick={() => setOpen(false)} />
          </div>
          <Divider orientation="horizontal" size="small" />
          <Form form={form} onFinish={onFinish}>
            <Form.List name={"columns"}>
              {(fields) =>
                fields.map((field) => (
                  <Form.Item name={field.name} noStyle>
                    <SwitchItem />
                  </Form.Item>
                ))
              }
            </Form.List>
            <Divider orientation="horizontal" size="small" />
            <div style={{ textAlign: "end" }}>
              <Space size="small">
                <Button size="small" type="primary" htmlType="submit">
                  Applica
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      }
    >
      {children}
    </Popover>
  );
};

const SortColumnPopover = ({ items, value, onChange, children }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(value);
  }, [value]);

  const onFinish = (f) => {
    setOpen(false);
    onChange(f);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottom"
      content={
        <div style={{ minWidth: 250 }}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Typography.Text strong style={{ flex: 1 }}>
              Ordina per
            </Typography.Text>
            <Button icon={<CloseOutlined />} onClick={() => setOpen(false)} size="small" />
          </div>
          <Divider orientation="horizontal" size="small" />
          <Form form={form} onFinish={onFinish}>
            <Form.Item name={"sortField"} noStyle>
              <Radio.Group vertical>
                {items
                  .filter((x) => x.sorter)
                  .map((item) => (
                    <Radio value={item.key}>{item.title}</Radio>
                  ))}
              </Radio.Group>
            </Form.Item>
            <Divider orientation="horizontal" size="small" />
            <Form.Item noStyle name={"sortOrder"}>
              <Radio.Group vertical>
                <Radio value={"ascend"}>
                  <SortAscendingOutlined /> Crescente
                </Radio>
                <Radio value={"descend"}>
                  <SortDescendingOutlined /> Decrescente
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Divider orientation="horizontal" size="small" />
            <div style={{ textAlign: "end" }}>
              <Space size="small">
                <Button
                  size="small"
                  onClick={() => {
                    form.resetFields();
                    onChange({ sortField: undefined, sortOrder: undefined });
                    setOpen(false);
                  }}
                >
                  Reset
                </Button>
                <Button size="small" type="primary" htmlType="submit">
                  Applica
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      }
    >
      {children}
    </Popover>
  );
};

const isAnyFilterSet = (filterItems, value) =>
  filterItems.filter((x) => !!value[x.name]).length > 0;

const isSortSet = (value) => {
  return !!value.sortField;
};

const SelectView = (id) => {
  const tableViews = useTableView(id);
  const [views, setViews] = useState([]);
  useEffect(() => {
    tableViews.list().then((res) => {
      setViews(res ?? []);
    });
  }, []);
  const allElements = { key: "all", label: "Tutti gli elementi" };
  return (
    <Dropdown
      menu={{
        items: [
          {
            key: "grp",
            type: "group",
            label: "Viste",
            children: [allElements, ...views.map((x) => ({ key: x.id, label: x.name }))],
          },
          { type: "divider" },
          { key: "saveAs", label: "Salva con nome" },
        ],
      }}
    >
      <Button iconPlacement={"end"} icon={<DownOutlined style={{ fontSize: "0.8em" }} />}>
        Tutti gli elementi
      </Button>
    </Dropdown>
  );
};

export default function FilterRow({
  items,
  onChange,
  value,
  style,
  columns,
  onSearch = () => {},
  exportFn,
  extra,
  onVisibleColumnsChange,
  visibleColumns,
}) {
  const [open, setOpen] = useState(false);
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  const filterItems = items.filter;
  const sortItems = items.sort;
  const columnItems = items.column;
  const hasSortingColumns = columns.filter((x) => x.sorter).length > 0;

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(value);
  }, [value]);

  const handleOnFinish = (form) => {
    onChange(form);
    setOpen(false);
  };

  const getItem = (name) => filterItems.filter((x) => x.name === name)[0];

  const renderTags = () => {
    return filterItems
      .filter((fi) => !!value[fi.name])
      .map((fi) => (
        <Tag
          key={fi.name}
          closable
          variant="outlined"
          className="big-tag"
          onClose={() => onChange({ ...value, [fi.name]: undefined })}
        >
          {fi.label}: {fi.readableValue?.(value[fi.name]) ?? value[fi.name]}
        </Tag>
      ));
  };

  return (
    <div style={{ ...style, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", flexDirection: "row", columnGap: 10 }}>
        <div style={{ flex: 1 }}>
          <Input
            allowClear
            onClear={() => onSearch?.("")}
            onPressEnter={(e) => onSearch?.(e.target.value)}
            prefix={<SearchOutlined style={{ color: token.colorBorder }} />}
            style={{ maxWidth: 250 }}
            placeholder="Cerca"
          />
        </div>
        <Space size="small">
          <Space
            size="small"
            separator={<Divider orientation={"vertical"} style={{ margin: 0 }} />}
          >
            {withTooltip(
              <Button
                color={isAnyFilterSet(filterItems, value) && "primary"}
                type={"text"}
                variant={isAnyFilterSet(filterItems, value) ? "filled" : "default"}
                icon={<FilterOutlined />}
                onClick={() => setOpen(true)}
              />,
              "Filtri",
            )}
            {hasSortingColumns && (
              <SortColumnPopover
                items={columns}
                value={value}
                onChange={(res) => onChange({ ...value, ...res })}
              >
                {withTooltip(
                  <Button
                    color={isSortSet(value) && "primary"}
                    type={"text"}
                    variant={isSortSet(value) ? "filled" : "default"}
                    icon={<ArrowDownUp size={16} />}
                  />,
                  "Ordina",
                )}
              </SortColumnPopover>
            )}
            <SelectColumnsPopover value={visibleColumns} onChange={onVisibleColumnsChange}>
              {withTooltip(
                <Button type={"text"} icon={<Columns3Cog size={16} />} />,
                "Seleziona colonne",
              )}
            </SelectColumnsPopover>
            {withTooltip(
              <Button icon={<ExportOutlined />} type="text" onClick={exportFn} />,
              "Esporta",
            )}
          </Space>

          {/*          <Button icon={<EllipsisOutlined />} />*/}
          <SelectView />
          {extra}
        </Space>
      </div>
      <div>
        <div
          style={{
            display: isAnyFilterSet(filterItems, value) ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <Divider orientation="horizontal" size="medium" />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              columnGap: 5,
              alignItems: "center",
            }}
          >
            {renderTags()}
            <Button type="text" onClick={() => onChange({})} icon={<DeleteOutlined />}>
              Rimuovi tutti
            </Button>
          </div>
        </div>
      </div>
      <Drawer forceRender open={open} closable onClose={() => setOpen(false)} title="Filtri">
        <Form
          onFinish={(form) => {
            handleOnFinish(form);
          }}
          initialValues={filterItems
            .filter((x) => x.initialValue != null)
            .reduce((acc, x) => ({ ...acc, [x.name]: [x.initialValue] }), {})}
          layout="vertical"
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
          form={form}
        >
          <div
            style={{
              flex: "1 1 0",
              overflow: "auto",
              scrollbarWidth: "thin",
              scrollbarGutter: "stable",
            }}
          >
            {(filterItems ?? []).map((x) => (
              <Form.Item key={x.name} name={x.name} label={x.label} {...x.formItem.props}>
                {x.formItem.children}
              </Form.Item>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              columnGap: 16,
            }}
          >
            <Button
              onClick={() => {
                form.resetFields();
                onChange({});
                setOpen(false);
              }}
            >
              Reset
            </Button>
            <Button type="primary" htmlType="submit">
              Applica
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}
